/**
 * Data Store
 * 
 * Centralized storage for mock data using localStorage.
 * This ensures all tabs/windows see the same data.
 */

import { StockItem } from "../types/stockItem";
import { WarehouseLocation } from "../types/warehouseLocation";

const STOCK_ITEMS_KEY = 'stock_items_data';
const LOCATIONS_KEY = 'warehouse_locations_data';
const VERSION_KEY = 'data_version';
const CURRENT_VERSION = '1.1'; // Bumped to force data refresh with date fix

/**
 * Reviver function to convert date strings back to Date objects
 */
const dateReviver = (key: string, value: any): any => {
  // List of known date fields in our data models
  const dateFields = [
    'lastMovementDate',
    'lastUpdated',
    'createdAt',
    'updatedAt',
    'date',
    'timestamp',
    'expiryDate',
    'manufactureDate',
  ];

  if (typeof value === 'string' && dateFields.includes(key)) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date;
  }
  return value;
};

/**
 * Store stock items in localStorage
 */
export const storeStockItems = (items: StockItem[]): void => {
  try {
    localStorage.setItem(STOCK_ITEMS_KEY, JSON.stringify(items));
    localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
  } catch (error) {
    console.error('[DataStore] Error storing stock items:', error);
  }
};

/**
 * Get stock items from localStorage
 */
export const getStoredStockItems = (): StockItem[] | null => {
  try {
    const version = localStorage.getItem(VERSION_KEY);
    if (version !== CURRENT_VERSION) {
      return null;
    }

    const data = localStorage.getItem(STOCK_ITEMS_KEY);
    if (!data) return null;

    return JSON.parse(data, dateReviver);
  } catch (error) {
    console.error('[DataStore] Error getting stock items:', error);
    return null;
  }
};

/**
 * Store warehouse locations in localStorage
 */
export const storeLocations = (locations: WarehouseLocation[]): void => {
  try {
    localStorage.setItem(LOCATIONS_KEY, JSON.stringify(locations));
  } catch (error) {
    console.error('[DataStore] Error storing locations:', error);
  }
};

/**
 * Get warehouse locations from localStorage
 */
export const getStoredLocations = (): WarehouseLocation[] | null => {
  try {
    const version = localStorage.getItem(VERSION_KEY);
    if (version !== CURRENT_VERSION) {
      return null;
    }

    const data = localStorage.getItem(LOCATIONS_KEY);
    if (!data) return null;

    return JSON.parse(data, dateReviver);
  } catch (error) {
    console.error('[DataStore] Error getting locations:', error);
    return null;
  }
};

/**
 * Clear all stored data
 */
export const clearStoredData = (): void => {
  try {
    localStorage.removeItem(STOCK_ITEMS_KEY);
    localStorage.removeItem(LOCATIONS_KEY);
    localStorage.removeItem(VERSION_KEY);
  } catch (error) {
    console.error('[DataStore] Error clearing data:', error);
  }
};

/**
 * Check if data is stored
 */
export const hasStoredData = (): boolean => {
  try {
    const version = localStorage.getItem(VERSION_KEY);
    if (version !== CURRENT_VERSION) {
      return false;
    }

    return !!(
      localStorage.getItem(STOCK_ITEMS_KEY) &&
      localStorage.getItem(LOCATIONS_KEY)
    );
  } catch {
    return false;
  }
};
